import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, orderBy } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Bell, LogOut, Users, Award, AlertTriangle, CheckCircle, XCircle, Printer, Eye, ExternalLink, X } from 'lucide-react';
import { createStudentViewPath } from '../../utils/studentRoutes';

function MentorDashboard() {
  const navigate = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [mentees, setMentees] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pendingAchievements, setPendingAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  useEffect(() => {
    loadMentorData();
    loadNotifications();

    // Set up real-time notifications listener
    const interval = setInterval(loadNotifications, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      console.log('🔔 Loading notifications for:', currentUser.email);

      // Load notifications from sentMessages collection
      const notificationsQuery = query(
        collection(db, 'sentMessages'),
        where('toEmail', '==', currentUser.email),
        where('isRead', '==', false)
      );
      
      const notificationsSnapshot = await getDocs(notificationsQuery);
      console.log('📬 Found documents:', notificationsSnapshot.size);
      
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by createdAt on client side
      notificationsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });

      console.log('✅ Notifications loaded:', notificationsData);
      setNotifications(notificationsData);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'sentMessages', notificationId), {
        isRead: true
      });
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const loadMentorData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/staff-login');
        return;
      }

      // Load mentor profile
      const mentorDoc = await getDoc(doc(db, 'users', currentUser.email));
      if (mentorDoc.exists()) {
        const mentorData = mentorDoc.data();
        setMentor(mentorData);

        // Load mentees
        const menteesQuery = query(
          collection(db, 'students'),
          where('batch', '==', mentorData.assignedBatch)
        );
        const menteesSnapshot = await getDocs(menteesQuery);
        const menteesData = [];

        for (const studentDoc of menteesSnapshot.docs) {
          const studentData = studentDoc.data();
          
          // Load attendance for each student
          const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
          const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;

          menteesData.push({
            ...studentData,
            attendance: attendanceData
          });
        }

        setMentees(menteesData);

        // Load pending achievements
        const achievementsQuery = query(
          collection(db, 'achievements'),
          where('status', '==', 'pending')
        );
        const achievementsSnapshot = await getDocs(achievementsQuery);
        const batchStudentRollNumbers = menteesData.map(m => m.rollNumber);
        const pendingAchievementsData = achievementsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(ach => batchStudentRollNumbers.includes(ach.rollNumber));

        setPendingAchievements(pendingAchievementsData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading mentor data:', error);
      setLoading(false);
    }
  };

  const handleVerifyAchievement = async (achievementId, status, reason = null) => {
    try {
      await updateDoc(doc(db, 'achievements', achievementId), {
        status: status,
        verifiedBy: mentor.email,
        verificationDate: new Date(),
        rejectionReason: reason
      });

      // Remove from pending list
      setPendingAchievements(pendingAchievements.filter(a => a.id !== achievementId));
      
      alert(`Achievement ${status === 'verified' ? 'verified' : 'rejected'} successfully!`);
      loadMentorData();
    } catch (error) {
      console.error('Error verifying achievement:', error);
      alert('Failed to verify achievement. Please try again.');
    }
  };

  const generateWarningLetter = (student) => {
    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('en-GB');
    const letterHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Attendance Deficiency Notice - ${student.rollNumber}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0;
              width: 210mm;
              min-height: 297mm;
            }
            .no-print { display: none; }
          }
          body { 
            max-width: 210mm;
            margin: 0 auto; 
            padding: 15mm;
            font-family: 'Times New Roman', Times, serif;
            font-size: 11pt;
            line-height: 1.5;
            position: relative;
          }
          .letterhead {
            text-align: center;
            margin-bottom: 20px;
          }
          .letterhead img {
            width: 100%;
            max-width: 800px;
          }
          .date-right {
            text-align: right;
            margin-bottom: 15px;
            font-size: 11pt;
          }
          .title { 
            text-align: center; 
            color: #c00; 
            font-weight: bold; 
            margin: 20px 0 15px 0;
            font-size: 13pt;
            text-decoration: underline;
          }
          .content { 
            text-align: justify;
            margin-bottom: 12px;
            font-size: 11pt;
          }
          .ref-section {
            margin-bottom: 15px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0;
          }
          th, td { 
            border: 2px solid #000; 
            padding: 6px 10px; 
            text-align: left;
            font-size: 10pt;
          }
          th { 
            background-color: #f0f0f0;
            font-weight: bold;
          }
          .red-text {
            color: #c00;
            font-weight: bold;
          }
          .important { 
            margin: 15px 0;
          }
          .important li { 
            margin: 6px 0;
            text-align: justify;
          }
          .signatures {
            margin-top: 40px;
          }
          .signature-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 60px;
          }
          .signature-block {
            width: 30%;
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #000;
            margin-top: 40px;
            padding-top: 5px;
            font-size: 10pt;
          }
          .signatures {
            margin-top: 50px;
            margin-bottom: 30px;
          }
          .signature-row {
            display: flex;
            justify-content: space-between;
          }
          .signature-block {
            width: 30%;
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #000;
            margin-top: 0px;
            padding-top: 5px;
            font-size: 10pt;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 8pt;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 8px;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background-color: #c00;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14pt;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            z-index: 1000;
          }
          .print-button:hover {
            background-color: #a00;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">🖨️ Print Letter</button>
        
        <div class="letterhead">
          <img src="/ybit-letterhead.png" alt="YBIT Letterhead">
        </div>
        
        <div class="date-right">
          <strong>Date:</strong> ${today}
        </div>
        
        <h2 class="title">ATTENDANCE DEFICIENCY NOTICE</h2>
        
        <div class="ref-section">
          <p style="margin: 5px 0;">Ref. No.: ___________________________</p>
        </div>
        
        <div class="content">
          <p><strong>To,</strong><br>
          The Parent/Guardian of<br>
          <strong>${student.firstName} ${student.surname}</strong><br>
          Roll No: <strong>${student.rollNumber}</strong><br>
          Class: <strong>${student.division}</strong></p>
          
          <p><strong>Subject: Notification of Low Attendance - Urgent Action Required</strong></p>
          
          <p>Dear Parent/Guardian,</p>
          
          <p>This is to inform you that your ward, <strong>${student.firstName} ${student.surname}</strong> (Roll No: <strong>${student.rollNumber}</strong>), has been found <strong class="red-text">DEFICIENT</strong> in attendance for the current academic semester.</p>
          
          <p><strong>Current Attendance Status:</strong></p>
          
          <table>
            <tr>
              <th>Parameter</th>
              <th>Percentage</th>
            </tr>
            <tr>
              <td>Theory Attendance</td>
              <td>${parseFloat(student.attendance?.theoryPercentage || 0).toFixed(2)}%</td>
            </tr>
            <tr>
              <td>Practical Attendance</td>
              <td>${parseFloat(student.attendance?.practicalPercentage || 0).toFixed(2)}%</td>
            </tr>
            <tr>
              <td><strong>Overall Attendance</strong></td>
              <td class="red-text">${parseFloat(student.attendance?.overallPercentage || 0).toFixed(2)}%</td>
            </tr>
            <tr>
              <td><strong>Minimum Required Attendance</strong></td>
              <td><strong>75.00%</strong></td>
            </tr>
            <tr>
              <td><strong>Shortage</strong></td>
              <td class="red-text">${(75 - parseFloat(student.attendance?.overallPercentage || 0)).toFixed(2)}%</td>
            </tr>
          </table>
          
          <div class="important">
            <p><strong>Important Points to Note:</strong></p>
            <ol>
              <li><strong>University Regulation:</strong> As per Mumbai University norms, a minimum of <strong>75% attendance</strong> is mandatory to be eligible for appearing in the End Semester Examination (ESE).</li>
              <li><strong>Consequence of Low Attendance:</strong> Students with attendance below 75% will <strong>NOT be permitted</strong> to appear for End Semester Examinations, which may result in the student having to repeat the entire semester.</li>
              <li><strong>Academic Impact:</strong> This will cause significant academic delay and may affect the student's overall academic progression and career prospects.</li>
            </ol>
          </div>
          
          <p>We request your immediate attention to this matter and your cooperation in ensuring your ward's regular attendance. For any clarifications or concerns, please feel free to contact the undersigned.</p>
          
          <p>Thank you for your cooperation.</p>
        </div>
        
        <div class="signatures">
          <div class="signature-row">
            <div class="signature-block">
              <div class="signature-line">
                _________________________<br>
                <strong>Class Coordinator</strong>
              </div>
            </div>
            <div class="signature-block">
              <div class="signature-line">
                _________________________<br>
                <strong>Head of Department</strong><br>
                Dept. of Engg. Sciences & Humanities
              </div>
            </div>
            <div class="signature-block">
              <div class="signature-line">
                _________________________<br>
                <strong>Principal</strong><br>
                YBIT, Pune
              </div>
            </div>
          </div>
        </div>
        
        <div class="footer">
          Bhonsale Knowledge City, Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi, Dist: Sindhudurg 416510<br>
          DTE CODE: 3470 | MSBTE CODE: 1742
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(letterHTML);
    printWindow.document.close();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/staff-login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const openStudentDashboard = (student) => {
    // Store mentor access token in localStorage (works across tabs)
    const mentorAccess = {
      mentorEmail: mentor.email,
      mentorName: mentor.name,
      studentRollNumber: student.rollNumber,
      timestamp: new Date().getTime(),
      expiresIn: 3600000 // 1 hour in milliseconds
    };
    
    localStorage.setItem('mentorAccess', JSON.stringify(mentorAccess));
    
    // Open student dashboard in new tab with correct route
    window.open(createStudentViewPath('/student/dashboard', student.rollNumber, '?mentorView=true'), '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mentor dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mentor Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">
                {mentor?.name} | Batch {mentor?.assignedBatch}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg relative"
                >
                  <Bell className="w-6 h-6" />
                  {notifications.length > 0 && (
                    <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No new notifications
                      </div>
                    ) : (
                      <div className="divide-y max-h-96 overflow-y-auto">
                        {notifications.map(notification => (
                          <div key={notification.id} className="p-4 hover:bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <p className="font-semibold text-sm text-gray-900">
                                {notification.title || 'New Message'}
                              </p>
                              <button
                                onClick={() => markNotificationAsRead(notification.id)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600">{notification.message}</p>
                            {notification.fromName && (
                              <p className="text-xs text-gray-500 mt-1">
                                From: {notification.fromName}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {notification.createdAt?.toDate ? 
                                new Date(notification.createdAt.toDate()).toLocaleString() : 
                                'Recently'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Mentees</p>
                <p className="text-3xl font-bold text-gray-900">{mentees.length}</p>
              </div>
              <Users className="w-12 h-12 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Attendance</p>
                <p className="text-3xl font-bold text-red-600">
                  {mentees.filter(m => m.attendance?.overallPercentage < 75).length}
                </p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Verifications</p>
                <p className="text-3xl font-bold text-orange-600">{pendingAchievements.length}</p>
              </div>
              <Award className="w-12 h-12 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Student List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">My Mentees</h2>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {mentees.map(student => (
                <button
                  key={student.rollNumber}
                  onClick={() => setSelectedStudent(student)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${
                    selectedStudent?.rollNumber === student.rollNumber
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {student.firstName} {student.surname}
                      </p>
                      <p className="text-sm text-gray-600">{student.rollNumber}</p>
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          student.attendance?.overallPercentage >= 75
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {parseFloat(student.attendance?.overallPercentage || 0).toFixed(2)}% Attendance
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Student Details */}
          <div className="md:col-span-2 space-y-6">
            {selectedStudent ? (
              <>
                {/* Student Info Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedStudent.firstName} {selectedStudent.surname}
                    </h2>
                    <button
                      onClick={() => openStudentDashboard(selectedStudent)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="View Mentee's Dashboard"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Visit Mentee's Dashboard</span>
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Roll Number</p>
                      <p className="font-semibold">{selectedStudent.rollNumber}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Division / Batch</p>
                      <p className="font-semibold">{selectedStudent.division} / {selectedStudent.batch}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Email</p>
                      <p className="font-semibold">{selectedStudent.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Mobile</p>
                      <p className="font-semibold">{selectedStudent.mobile}</p>
                    </div>
                  </div>

                  {/* Attendance Section */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Attendance Summary</h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {parseFloat(selectedStudent.attendance?.theoryPercentage || 0).toFixed(2)}%
                        </p>
                        <p className="text-sm text-gray-600">Theory</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {parseFloat(selectedStudent.attendance?.practicalPercentage || 0).toFixed(2)}%
                        </p>
                        <p className="text-sm text-gray-600">Practical</p>
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${
                          selectedStudent.attendance?.overallPercentage >= 75 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {parseFloat(selectedStudent.attendance?.overallPercentage || 0).toFixed(2)}%
                        </p>
                        <p className="text-sm text-gray-600">Overall</p>
                      </div>
                    </div>
                  </div>

                  {/* Warning Letter Button */}
                  {selectedStudent.attendance?.overallPercentage < 75 && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <p className="font-semibold text-red-900 mb-2">
                            Attendance Below Required 75%
                          </p>
                          <p className="text-sm text-red-700 mb-3">
                            Warning letter can be generated and sent to parents.
                          </p>
                          <button
                            onClick={() => generateWarningLetter(selectedStudent)}
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            <Printer className="w-4 h-4" />
                            <span>Generate & Print Warning Letter</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a student to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Achievements Section */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Pending Achievement Verifications</h2>
            <Award className="w-6 h-6 text-orange-600" />
          </div>

          {pendingAchievements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No pending verifications</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingAchievements.map(achievement => (
                <div key={achievement.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-blue-600">
                          {achievement.rollNumber}
                        </span>
                        <span className="text-gray-600">|</span>
                        <span className="text-gray-900">{achievement.studentName}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {achievement.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {achievement.description}
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {achievement.category}
                        </span>
                        <span className="text-gray-500">
                          {achievement.subcategory}
                        </span>
                        <span className="text-gray-500">
                          {new Date(achievement.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      {achievement.documentUrl && (
                        <button
                          onClick={() => window.open(achievement.documentUrl, '_blank')}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 mb-2"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Document</span>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={() => handleVerifyAchievement(achievement.id, 'verified')}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Verify</span>
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Please provide a reason for rejection:');
                        if (reason) {
                          handleVerifyAchievement(achievement.id, 'rejected', reason);
                        }
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MentorDashboard;
